import Avo from '@susu/custom-made/Avo';
import { CardListType } from '@susu/custom-made/components/cardList/cardList';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { ThemeContext } from '@susu/custom-made/providers';
import { actions as globalActions } from '@susu/custom-made/providers/reducers/global';
import { actions as optionActions } from '@susu/custom-made/providers/reducers/option';
import { applyBusinessRulesForOptionSelection, isOptionHiddenByRule } from '@susu/custom-made/utils/businessRules';
import { Clients, Level } from '@susu/custom-made/utils/constants';
import type {
  BusinessRules,
  DefaultValues,
  HiddenOptionsAndValues,
  LayerDefinitions,
  Option,
  OptionsSelection,
  OptionValue,
  StylePreset,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { changeLevel, getConfigurationTypeBySection } from '@susu/custom-made/utils/navigation';
import {
  getAvoEventParams,
  getAvoEventParamsWithOption,
  getConfigurationTypeText,
  isExtraConfigurationInProduct,
  onEnter,
  shouldAddAdditonalProduct,
  isProductBaseStyleActive,
  shouldShowRemoveAdditionalProduct,
} from '@susu/custom-made/utils/utils';
import { stylePresetModalNormalFlow, stylePresetModalCustomizeFlow } from '@susu/custom-made/utils/staticContent';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import Carousel from '../baseStyleCarousel/baseStyleCarousel';
import Button, { ButtonSize, ButtonType } from '../button/button';
import type { typeCard } from '../card/card';
import Modal from '../modal/modal';
import { MonogramPanel } from '../monogramPanel/monogramPanel';
import type { Item } from '../navigation/navigation';
import Panel, { PanelSize } from '../panel/panel';
import Tooltip from '../tooltip/tooltip';
import styles from './optionPanel.module.scss';
import { useAdditionalProduct } from '@susu/custom-made/hooks/useAdditionalProduct';
import { datadogLogs } from '@datadog/browser-logs';
const SearchList = dynamic(() => import('../searchList/searchList'), {ssr: false});
const Navigation = dynamic(() => import('../navigation/navigation'), {ssr: false});
const CardList = dynamic(() => import('@susu/custom-made/components/cardList/cardList'), {ssr: false});
export const OptionPanel = ({
  navigation,
  extraProductHeader,
  stylePresets,
  options,
  optionValues,
  businessRules,
  defaultValues,
  layerDefinitions,
  isInPreloadFlow,
}: {
  navigation: React.ReactNode;
  extraProductHeader: React.ReactNode;
  stylePresets: StylePreset[];
  options: Option[];
  optionValues: typeOptionValue;
  businessRules: BusinessRules;
  defaultValues: DefaultValues;
  layerDefinitions: LayerDefinitions;
  isInPreloadFlow: boolean;
}) => {
  const defaultOptionValueKeys = Object.keys(optionValues);
  const { dispatch, state } = useContext(ThemeContext);
  const [isSearchShown, setIsSearchShown] = useState<boolean>(false);
  const [filteredOptionValueIds, setFilteredOptionValueIds] = useState<number[] | string[]>(defaultOptionValueKeys);
  const [isStylePresetModalOpen, setIsStylePresetModalOpen] = useState<boolean>(false);
  const viewSize = useViewSize();
  const cardlistRef = useRef<HTMLInputElement>(null);
  const scrollUp = useScrollUp();
  const { removeSelectedOptionValueFromMissingValues, isOptionWithoutSelection } = useMissingValues();
  const affectedConfigurationTypeId = useMemo(() => getConfigurationTypeBySection(state.section!), [state.section]);
  const { addAdditionalProductHandler, removeAdditionalProductHandler } = useAdditionalProduct();

  const stylePresetModal =
    isInPreloadFlow && !isProductBaseStyleActive(state.selectedBaseStyle, state.section)
      ? stylePresetModalCustomizeFlow
      : stylePresetModalNormalFlow;

  const optionNavigationClick = (option: Option | Item) => {
    const tmpOption = option as Option;
    dispatch({
      type: optionActions.SET_ANIMATION_STATUS,
      payload: { status: true, from: state.selectedOption, to: tmpOption },
    });

    dispatch({
      type: optionActions.SET_SELECTED_OPTION,
      payload: tmpOption,
    });
    cardlistRef.current?.scrollTo(0, 0);

    const cmOption = `${state.selectedOptionGroup?.name}_${tmpOption.name}`;
    if (tmpOption?.isAdvanced) {
      state.client === Clients.onLine && Avo.advancedSectionSelect(getAvoEventParamsWithOption(state, cmOption));
    } else {
      state.client === Clients.onLine && Avo.basicSectionSelect(getAvoEventParamsWithOption(state, cmOption));
    }
  };

  const optionNavigation = (
    <>
      {state.selectedOptionGroup && state.selectedOption && (
        <nav>
          <Navigation
            items={[
              ...state.selectedOptionGroup.options!.map(option => {
                if (!isOptionHiddenByRule(option.id, state.hiddenOptionsAndValues, affectedConfigurationTypeId)) {
                  return {
                    text: option.name,
                    id: option.id.toString(),
                    option: option,
                    isActive: state.selectedOption!.id === option.id,
                    hasMissingValues: isOptionWithoutSelection(option, affectedConfigurationTypeId),
                  } as Item;
                }

                return undefined;
              }),
            ]}
            testId="option-group-nav"
            onItemClick={optionNavigationClick}
          />
        </nav>
      )}
      {!state.selectedOptionGroup && state.selectedOption && (
        <div data-test-id={state.selectedOption.id} className={styles.optionTitle}>
          {state.selectedOption.name}
        </div>
      )}
    </>
  );

  const selectOption = useCallback(
    (card: OptionValue) => {
      if (!state.isManualSelectionDone) {
        dispatch({
          type: globalActions.SET_MANUAL_SELECTION,
          payload: true,
        });
      }

      const previousSelectedOptions = structuredClone(state.selectedOptions);
      const previousHiddenOptionsAndValues = structuredClone(state.hiddenOptionsAndValues);

      const selectedOptions: OptionsSelection = structuredClone(state.selectedOptions);

      const prevValueId = selectedOptions[affectedConfigurationTypeId][state.selectedOption!.id];

      selectedOptions[affectedConfigurationTypeId][state.selectedOption!.id] = card.id;

      const tmpHiddenOptionsAndValues: HiddenOptionsAndValues = structuredClone(state.hiddenOptionsAndValues);

      //remove prvious hiddens
      applyBusinessRulesForOptionSelection(
        selectedOptions,
        state.selectedFabric!.id,
        businessRules,
        tmpHiddenOptionsAndValues,
        optionValues,
        defaultValues,
        card.id,
        affectedConfigurationTypeId,
        options,
        state.product!,
        state.growthbookFeatures['show-logs'],
        prevValueId,
      );

      dispatch({
        type: optionActions.SET_HIDDEN_OPTIONS_AND_VALUES,
        payload: tmpHiddenOptionsAndValues,
      });
      dispatch({
        type: optionActions.SELECT_OPTION,
        payload: selectedOptions,
      });

      removeSelectedOptionValueFromMissingValues(affectedConfigurationTypeId, card);

      datadogLogs.logger.info('Custom made - Option Clicked' ,
        {customData: {
          previousSelectedOptions,
          previousHiddenOptionsAndValues,
          previousOptionValueId: prevValueId,
          selectedFabric: state.selectedFabric,
          selectedOptions: selectedOptions,
          hiddenOptionsAndValues: tmpHiddenOptionsAndValues,
          selectedOptionValue: card,
        }}
      );

      const cmOption =
        (state.selectedOptionGroup?.name ? `${state.selectedOptionGroup?.name}_` : '') +
        `${state.selectedOption?.name}_${
          optionValues[selectedOptions[affectedConfigurationTypeId][state.selectedOption!.id]]
            ? optionValues[selectedOptions[affectedConfigurationTypeId][state.selectedOption!.id]].name
            : ''
        }`;

      if (state.selectedOption?.isAdvanced) {
        state.client === Clients.onLine && Avo.advancedOptionSelect(getAvoEventParamsWithOption(state, cmOption));
      } else {
        state.client === Clients.onLine && Avo.basicOptionSelect(getAvoEventParamsWithOption(state, cmOption));
      }
    },
    [
      state.isManualSelectionDone,
      state.selectedOptions,
      state.selectedFabric!.id,
      state.selectedOptionGroup?.name,
      state.selectedOption,
      state.hiddenOptionsAndValues,
      optionValues,
      defaultValues,
      options,
      businessRules,
      state.product,
      state.growthbookFeatures['show-logs'],
      dispatch,
      removeSelectedOptionValueFromMissingValues,
    ],
  );

  const monogramCardClick = useCallback(
    (card: typeCard) => {
      changeLevel(Level.Monogram, dispatch);
      state.client === Clients.onLine && Avo.basicSectionSelect(getAvoEventParamsWithOption(state, card.name));
    },
    [state.client, dispatch, changeLevel],
  );

  const optionGroupCardClick = useCallback(
    (card: typeCard) => {
      let tmpSelectedOption: Option | undefined = undefined;
      (card as Option).options?.forEach(option => {
        if (
          !tmpSelectedOption &&
          !isOptionHiddenByRule(option.id, state.hiddenOptionsAndValues, affectedConfigurationTypeId)
        ) {
          tmpSelectedOption = option;
        }
      });
      dispatch({
        type: optionActions.SET_ANIMATION_STATUS,
        payload: { status: true, from: undefined, to: tmpSelectedOption },
      });
      changeLevel(Level.Subgroup, dispatch);

      if (tmpSelectedOption) {
        dispatch({
          type: optionActions.SET_SELECTED_OPTION_GROUP,
          payload: card as Option,
        });
        dispatch({
          type: optionActions.SET_SELECTED_OPTION,
          payload: tmpSelectedOption,
        });

        const cmOption = `${card.name}_${(tmpSelectedOption as Option).name}`;
        if ((tmpSelectedOption as Option).isAdvanced) {
          state.client === Clients.onLine && Avo.advancedSectionSelect(getAvoEventParamsWithOption(state, cmOption));
        } else {
          state.client === Clients.onLine && Avo.basicSectionSelect(getAvoEventParamsWithOption(state, cmOption));
        }
      }
    },
    [dispatch, changeLevel, state.hiddenOptionsAndValues, state.client],
  );

  const optionCardClick = useCallback(
    (card: typeCard) => {
      const tmpSelectedOption = card as Option;
      dispatch({
        type: optionActions.SET_ANIMATION_STATUS,
        payload: { status: true, from: undefined, to: tmpSelectedOption },
      });
      changeLevel(Level.Subgroup, dispatch);
      dispatch({
        type: optionActions.SET_SELECTED_OPTION_GROUP,
        payload: undefined,
      });
      dispatch({
        type: optionActions.SET_SELECTED_OPTION,
        payload: tmpSelectedOption,
      });

      const cmOption = tmpSelectedOption.name;
      if (tmpSelectedOption?.isAdvanced) {
        state.client === Clients.onLine && Avo.advancedSectionSelect(getAvoEventParamsWithOption(state, cmOption));
      } else {
        state.client === Clients.onLine && Avo.basicSectionSelect(getAvoEventParamsWithOption(state, cmOption));
      }
    },
    [dispatch, changeLevel, state.client],
  );

  const cardClick = useCallback(
    (card: typeCard) => {
      if (state.level === Level.Group) {
        if ((card as Option).type === 'MONOGRAM') {
          monogramCardClick(card);
        } else if ((card as Option).type === 'OPTION_GROUP') {
          optionGroupCardClick(card);
        } else {
          optionCardClick(card);
        }
      } else {
        selectOption(card as OptionValue);
      }
    },
    [state.level, monogramCardClick, optionGroupCardClick, optionCardClick, selectOption, dispatch],
  );

  const searchCardClick = useCallback(
    (card: typeCard) => {
      selectOption(card as OptionValue);
    },
    [selectOption],
  );

  const confirmClick = () => {
    const cmOption: string =
      (state.selectedOptionGroup?.name ? `${state.selectedOptionGroup?.name}_` : '') +
      `${state.selectedOption?.name}_${
        optionValues[state.selectedOptions[affectedConfigurationTypeId][state.selectedOption!.id]]
          ? optionValues[state.selectedOptions[affectedConfigurationTypeId][state.selectedOption!.id]].name
          : ''
      }`;

    if (state.selectedOption?.isAdvanced) {
      state.client === Clients.onLine && Avo.advancedOptionConfirm(getAvoEventParamsWithOption(state, cmOption));
    } else {
      state.client === Clients.onLine && Avo.basicOptionConfirm(getAvoEventParamsWithOption(state, cmOption));
    }
    dispatch({
      type: optionActions.SET_ANIMATION_STATUS,
      payload: { status: true, from: state.selectedOption, to: undefined },
    });
    changeLevel(Level.Group, dispatch);
    setTimeout(() => {
      cardlistRef.current?.scrollTo(0, 0);
    }, 400);
  };
  const getProductBaseStyle = () => {
    const selectedStylePreset = state.selectedBaseStyle[affectedConfigurationTypeId];

    return selectedStylePreset?.name ?? 'No selected preset.';
  };

  const baseStyleClick = () => {
    setIsStylePresetModalOpen(true);
    state.client === Clients.onLine &&
      Avo.styleOptionChange(getAvoEventParamsWithOption(state, getProductBaseStyle() ?? ''));
  };

  const changeStyleClick = () => {
    setIsStylePresetModalOpen(false);
    dispatch({
      type: optionActions.CHANGE_BASE_STYLE_STATUS,
      payload: true,
    });

    state.client === Clients.onLine &&
      Avo.styleOptionChangeConfirm(getAvoEventParamsWithOption(state, getProductBaseStyle() ?? ''));
  };

  const changeStyleCancel = () => {
    setIsStylePresetModalOpen(false);
    state.client === Clients.onLine &&
      Avo.styleOptionChangeCancel(getAvoEventParamsWithOption(state, getProductBaseStyle() ?? ''));
  };

  const shouldShowBaseStyleSelection = () => {
    return stylePresets.length > 0 && state.isBaseStyleSelection;
  };

  const openSearchPanel = () => {
    setIsSearchShown(true);
    state.client === Clients.onLine && Avo.searchClicked(getAvoEventParams(state));
  };

  useEffect(() => {
    if (state.level === Level.Group) {
      setIsSearchShown(false);
      setIsStylePresetModalOpen(false);
    }
  }, [state.level]);

  useEffect(() => {
    if (state.selectedOption) {
      setFilteredOptionValueIds(state.selectedOption.optionValues!);
    }
  }, [state.selectedOption, setFilteredOptionValueIds]);

  return (
    <>
      {!shouldAddAdditonalProduct(state) && shouldShowBaseStyleSelection() && state.section && (
        <div>
          <Carousel
            stylePresets={stylePresets}
            section={state.section}
            optionValues={optionValues}
            layerDefinitions={layerDefinitions}
            businessRules={businessRules}
            options={options}
          />
        </div>
      )}
      {shouldAddAdditonalProduct(state) && (
        <div
          className={cn(styles.sectionMain, {
            [styles.isActive]: state.level !== Level.Subgroup,
          })}
        >
          {navigation}
          <div className={styles.addRemoveContainer}>
            <Modal
              isOpen={
                !state.isAdditionalProductAdded[affectedConfigurationTypeId] &&
                !isExtraConfigurationInProduct(state.product!, affectedConfigurationTypeId)
              }
              hasOverlay={false}
              title={`Add a ${getConfigurationTypeText(affectedConfigurationTypeId).toLowerCase()}`}
              price={
                '+' +
                (state.selectedFabric &&
                  state.selectedFabric.prices?.symbol +
                    state.selectedFabric.prices?.configurationTypes[affectedConfigurationTypeId])
              }
              description={`Elevate your ${state.product ?? ''}'s versatility with a ${getConfigurationTypeText(
                affectedConfigurationTypeId,
              )} for formal occasions`}
              firstButtonText={`Add ${getConfigurationTypeText(affectedConfigurationTypeId).toLowerCase()}`}
              firstButtonType={ButtonType.Secondary}
              isRectangular={false}
              isButtonContentSize={true}
              onFirstButtonClick={() => addAdditionalProductHandler(affectedConfigurationTypeId)}
              testId="add-additional-product-modal"
            />
          </div>
        </div>
      )}

      {!shouldAddAdditonalProduct(state) && !shouldShowBaseStyleSelection() && (
        <>
          {/* Group section */}
          <div
            className={cn(styles.sectionMain, {
              [styles.isActive]: state.level !== Level.Subgroup,
            })}
          >
            {navigation}
            {extraProductHeader}
            <div className={styles.cardListContainer}>
              <CardList
                cardItems={options}
                cardClick={cardClick}
                isGroup={true}
                isBaseStyleActive={
                  isProductBaseStyleActive(state.selectedBaseStyle, state.section) ||
                  (isInPreloadFlow && stylePresets.length > 0)
                }
                baseStyleClick={baseStyleClick}
                cardListType={CardListType.Option}
                optionValues={optionValues}
                showAdvancedButton={options.filter((option: Option) => option.isAdvanced === true).length > 0}
                showAdvancedItems={state.client === Clients.inStore}
                showRemoveAdditionalProductButton={shouldShowRemoveAdditionalProduct(state)}
                additionalProductRemoveClick={() => removeAdditionalProductHandler(affectedConfigurationTypeId)}
                testId="option-group-list"
                section={state.section}
                affectedConfigurationTypeId={affectedConfigurationTypeId}
              />
            </div>
          </div>
          {/* Group section */}

          {/* SubGroup section */}
          <div
            className={cn(styles.sectionNext, {
              [styles.isActive]: state.level === Level.Subgroup,
            })}
          >
            <div className={styles.header}>{optionNavigation}</div>
            {state.selectedOption?.hasSearch && (
              <div
                className={cn(styles.containerTop, {
                  [styles.show]: scrollUp.scrollingUp,
                })}
              >
                <div
                  className={styles.searchContainer}
                  data-test-id="option-panel-search-btn"
                  onClick={openSearchPanel}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    onEnter(e, openSearchPanel);
                  }}
                >
                  <i className="susu-icons" translate="no">
                    search
                  </i>
                  <span className={styles.text}>Search</span>
                </div>
              </div>
            )}
            <div className={styles.buttonContainer}>
              <Button
                isFullSize={true}
                onClick={confirmClick}
                type={ButtonType.IconOnlyRound}
                size={ButtonSize.Small}
                testId="option-panel-confirm-btn-mobile"
              >
                <div className={styles.buttonContent}>
                  <div className={styles.buttonIcon}>
                    <i className="susu-icons" translate="no">
                      arrow_left
                    </i>
                  </div>
                </div>
              </Button>
            </div>
            <div
              className={cn(styles.cardListContainer, {
                [styles.isSubList]: state.level === Level.Subgroup,
                [styles.hasSearch]: state.selectedOption?.hasSearch,
              })}
              ref={cardlistRef}
              onScroll={e => {
                scrollUp.handleScroll(e, 43);
              }}
            >
              <CardList
                cardItems={optionValues}
                cardSubItems={filteredOptionValueIds}
                selectedItemId={
                  state.selectedOption?.id
                    ? state.selectedOptions[affectedConfigurationTypeId][state.selectedOption?.id]
                    : null
                }
                cardClick={cardClick}
                isGroup={false}
                cardListType={CardListType.Option}
                testId="option-subgroup-list"
                section={state.section}
                selectedOption={state.selectedOption}
                affectedConfigurationTypeId={affectedConfigurationTypeId}
              />
            </div>
            {/* Subgroup section */}
          </div>

          {/* Search Panel */}
          <Panel
            isActive={isSearchShown}
            closeClick={() => {
              setIsSearchShown(false);
            }}
            panelSize={PanelSize.Multi}
          >
            {isSearchShown && (
              <SearchList
                onClose={() => {
                  setIsSearchShown(false);
                }}
                itemList={optionValues}
                filteredItemListIds={filteredOptionValueIds}
                selectedItemId={state.selectedOption?.id ?? null}
                cardClick={searchCardClick}
                cardListType={CardListType.Option}
                showScanner={false}
                selectedOption={state.selectedOption}
                affectedConfigurationTypeId={affectedConfigurationTypeId}
              />
            )}
          </Panel>
          <MonogramPanel
            isMonogramShown={state.level === Level.Monogram}
            options={options}
            configurationTypeId={affectedConfigurationTypeId}
          />
        </>
      )}
      <Modal
        isOpen={isStylePresetModalOpen}
        title={stylePresetModal.title}
        description={stylePresetModal.description}
        firstButtonText={stylePresetModal.firstButtonText}
        firstButtonType={ButtonType.SecondaryAlert}
        secondButtonText={stylePresetModal.secondButtonText}
        secondButtonType={ButtonType.Tertiary}
        onFirstButtonClick={changeStyleClick}
        onSecondButtonClick={changeStyleCancel}
        onOverlayClick={changeStyleCancel}
        isFullSize={true}
        testId="section-change-style-preset-modal"
      />
      {(viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet) && <Tooltip />}
    </>
  );
};
